import moment from "moment";
import { buildCommentAuthorFromStorage } from "../../shared/utils/commentAuthor";

export function normalizeContainerObjectIds(containerObjectIdList) {
  if (!Array.isArray(containerObjectIdList)) {
    return [];
  }
  return containerObjectIdList
    .map((entry) => {
      if (entry == null) {
        return "";
      }
      if (typeof entry === "string") {
        return entry;
      }
      if (entry._id != null) {
        return String(entry._id);
      }
      return String(entry);
    })
    .filter((id) => /^[a-f0-9]{24}$/i.test(id));
}

export function resolveMobileCameraContainerId(camera) {
  const containerIds = normalizeContainerObjectIds(
    camera?.containerObjectIdList,
  );

  if (containerIds.length === 1) {
    return { containerId: containerIds[0] };
  }
  if (containerIds.length === 0) {
    return { error: "none" };
  }
  return { error: "multiple", containerIds };
}

/**
 * Same rule as Activity / characterization: client metadata (weight, comment,
 * flux, origin) is stored under the super-container id when the physical
 * container belongs to one.
 */
export function resolveClientMeasurementContainerId(
  containerId,
  superContainers = [],
) {
  const physicalId = String(containerId ?? "").trim();
  if (!physicalId) {
    return physicalId;
  }

  const list = Array.isArray(superContainers) ? superContainers : [];
  const parent = list.find((superContainer) =>
    normalizeContainerObjectIds(superContainer?.containerObjectIdList).includes(
      physicalId,
    ),
  );

  return parent?._id != null ? String(parent._id) : physicalId;
}

export function buildCaptureTimestampPair(captureDate = new Date()) {
  const captureTime = `${captureDate.toISOString().split(".")[0]}Z`;
  const fileTimestamp = captureTime.slice(0, -1).replace(/:/g, "_");
  return { captureTime, fileTimestamp };
}

export function buildRawImageBlobName(cameraId, fileTimestamp) {
  const imageName = `${fileTimestamp}-camera-${cameraId}`;
  return {
    imageName,
    blobName: `camera-${cameraId}/raw/${imageName}-raw.jpeg`,
  };
}

export function buildOutputClientBlobName(
  cameraId,
  fileTimestamp,
  containerId,
) {
  return `camera-${cameraId}/output-client/${fileTimestamp}-camera-${cameraId}-${containerId}-client.json`;
}

export function buildWasteOriginSelectOptions(clients) {
  const dedupedClients = (clients ?? []).reduce(
    (acc, client) => {
      const key = `${client.name}-${client.address}`;
      if (!acc.keys.has(key)) {
        acc.keys.add(key);
        acc.items.push(client);
      }
      return acc;
    },
    { keys: new Set(), items: [] },
  ).items;

  return dedupedClients.map((item) => ({
    key: item._id ?? `${item.name}-${item.address}`,
    value: JSON.stringify(item),
    label: `${item.name} (${
      item?.address &&
      item.address !== " " &&
      ![undefined, "undefined"].includes(item.address)
        ? item.address
        : "-"
    })`,
  }));
}

export function buildClientPayloadForOutput({
  selectedClientJson,
  identificationReference,
}) {
  const hasClient = Boolean(String(selectedClientJson ?? "").trim());
  const hasReference = Boolean(String(identificationReference ?? "").trim());

  if (!hasClient && !hasReference) {
    return { clientPayload: "", mongoClientId: undefined };
  }

  const parsed = hasClient ? JSON.parse(selectedClientJson) : {};

  return {
    clientPayload: JSON.stringify({
      name: parsed.name ?? "",
      address: parsed.address ?? "",
      code: hasReference
        ? String(identificationReference).trim()
        : parsed.code ?? "",
      _id: parsed._id,
      checkStatus: parsed.checkStatus ?? "-",
      tags: parsed.tags ?? [],
    }),
    mongoClientId: parsed._id,
  };
}

export function buildWasteCategorySelectOptions(wasteCategories) {
  return (wasteCategories ?? []).map((item) => ({
    key: item._id,
    value: JSON.stringify({
      id: item._id,
      wasteCategoryName: item.label,
      wasteCategoryCode: item.wasteCodeFull,
      wasteCategoryValue: item.value,
      wasteCategoryAuthorizedMaterial: item.authorizedMaterial,
      wasteCategoryDensity: item.density,
    }),
    label: item.label,
  }));
}

export function tryParseSelectedWasteCategory(selectedWasteCategoryJson) {
  if (!String(selectedWasteCategoryJson ?? "").trim()) {
    return null;
  }
  return JSON.stringify(JSON.parse(selectedWasteCategoryJson));
}

export function shouldWriteOutputClient({
  selectedClientJson,
  selectedWasteCategoryJson,
  identificationReference,
  weightInput,
  commentText,
}) {
  return (
    Boolean(String(selectedClientJson ?? "").trim()) ||
    Boolean(String(selectedWasteCategoryJson ?? "").trim()) ||
    Boolean(String(identificationReference ?? "").trim()) ||
    isWeightInputValid(weightInput) ||
    Boolean(String(commentText ?? "").trim())
  );
}

export function formatPhotoCaptureCommentSubtitle(
  comment,
  placeholder,
  maxLength = 52,
) {
  const trimmed = String(comment ?? "").trim();
  if (!trimmed) {
    return placeholder;
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength)}…`;
}

export function buildPhotoCaptureCommentsList(commentText) {
  const trimmed = String(commentText ?? "").trim();
  if (!trimmed) {
    return JSON.stringify([]);
  }

  return JSON.stringify([
    {
      time: new Date().toISOString(),
      author: buildCommentAuthorFromStorage(),
      text: trimmed,
    },
  ]);
}

export function buildMetadataSummary({
  t,
  clientOptions,
  wasteCategoryOptions,
  selectedClient,
  selectedWasteCategory,
  identificationReference,
  weightTonnes,
  commentText,
}) {
  const parts = [];
  const originOption = clientOptions.find(
    (option) => option.value === selectedClient,
  );
  if (originOption) {
    parts.push(originOption.label);
  }
  const wasteOption = wasteCategoryOptions.find(
    (option) => option.value === selectedWasteCategory,
  );
  if (wasteOption) {
    parts.push(wasteOption.label);
  }
  if (String(identificationReference ?? "").trim()) {
    parts.push(String(identificationReference).trim());
  }
  if (isWeightInputValid(weightTonnes)) {
    parts.push(`${String(weightTonnes).trim()} ${t("tons")}`);
  }
  const trimmedComment = String(commentText ?? "").trim();
  if (trimmedComment) {
    const preview =
      trimmedComment.length > 40
        ? `${trimmedComment.slice(0, 40)}…`
        : trimmedComment;
    parts.push(preview);
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function isWeightInputValid(weightInput) {
  const normalized = String(weightInput ?? "")
    .trim()
    .replace(",", ".");
  if (normalized === "") {
    return false;
  }
  if (!/^[0-9]+([.][0-9]+)?$/.test(normalized)) {
    return false;
  }
  const numericWeight = parseFloat(normalized);
  return !Number.isNaN(numericWeight) && numericWeight >= 0;
}

export function parseWeightTonnesToKg(weightInput) {
  const normalized = String(weightInput).trim().replace(",", ".");
  return parseFloat(normalized) * 1000;
}

export function hasInvalidWeightInput(weightInput) {
  const trimmed = String(weightInput ?? "").trim();
  if (trimmed === "") {
    return false;
  }
  return !isWeightInputValid(weightInput);
}

export function resolveWeightKgFromTonnesInput(weightInput) {
  if (!isWeightInputValid(weightInput)) {
    return undefined;
  }
  return parseWeightTonnesToKg(weightInput);
}

export function buildOutputClientPayload({
  containerId,
  captureTime,
  blobName,
  clientPayload = "",
  wasteCategoryPayload = "",
  mongoClientId,
  weightKg,
  comments = JSON.stringify([]),
}) {
  const payload = {
    id: containerId,
    time: captureTime,
    modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
    blobName,
    client: clientPayload,
    waste_category: wasteCategoryPayload,
    comments,
  };

  if (weightKg !== undefined && weightKg !== null) {
    payload.weight = weightKg;
  }
  if (mongoClientId) {
    payload.mongoClientId = mongoClientId;
  }

  return payload;
}
